import React from "react";
import CustomTabContainer from "../CustomTabContainer";
import CustomTab from "../CustomTab";
import ButtonAction from "../buttons/ButtonAction";
import { TrashOutlineIcon } from "../buttons/IconButtons";
import { SEARCH_MODEL } from "../table/Model/TableModel";
import AddTabResource from "./SubComponents/AddTabResource";
import AssignedTabResource from "./SubComponents/AssignedTabResource";
import { TabPanelApp } from "./Functionality/TabPanelApp";
import { TAB_PANEL_MODEL } from "./Model/TabPanelModel";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Generic Add/Assigned tab panel for a shift-resource section. Top header with a
 * bulk-delete action on the Assigned tab, then the Add (catalog search) or
 * Assigned (local selection) view. Domain-agnostic and reused across modules:
 * each caller binds its resource config through the `useAddApp` / `useAssignedApp`
 * hooks and the tab definitions (`tabsConfig`).
 *
 * @param {Object} props
 * @param {string} props.resource - Resource type handled by this section.
 * @param {Object} props.tabsConfig - Tab definitions, exposing getTabsById(id).
 * @param {Array<Object>} props.selected - Section selection list.
 * @param {Function} props.setSelected - Section selection setter.
 * @param {Array} props.selectForDelete - Delete buffer for the Assigned tab.
 * @param {Function} props.setSelectForDelete - Delete buffer setter.
 * @param {number|string|null|undefined} props.id - Shift id (edit mode).
 * @param {string} props.deleteButtonId - DOM id for the bulk-delete button.
 * @param {Function} props.useAddApp - Domain hook for the Add tab config.
 * @param {Function} props.useAssignedApp - Domain hook for the Assigned tab config + handlers.
 * @param {boolean} props.emptyFields - When true, flags the Add tab selected section red (empty required selection on next/update).
 * @param {boolean} props.readOnly - When true, renders only the assigned list: no Add tab, no bulk delete and no row selection.
 * @param {Function} [props.setDisplayModal] - Display setter of the modal holding the section, it is hidden while the delete confirmation of the Assigned tab is open.
 * @param {boolean} [props.autoHeight=true] - Forwarded to both tabs; set it to false when the panel lives inside a modal, where the viewport-based height does not apply.
 */
const TabPanel = ({
    resource,
    tabsConfig,
    selected,
    setSelected,
    selectForDelete,
    setSelectForDelete,
    searchElement,
    setSearchElement,
    modified,
    setModified,
    id,
    deleteButtonId,
    useAddApp,
    useAssignedApp,
    mainContainerClass='',
    emptyFields=false,
    disabled=false,
    setDisplayModal,
    autoHeight=true
}) => {

    const {
        t,
        subViewChild, setSubViewChild,
        results, setResults,
        setBodyForExport,
        setSearch,
        setSimpleSearch,
        triggerDelete, setTriggerDelete,
        isAllChecked, setIsAllChecked,
    } = TabPanelApp(disabled ? TAB_PANEL_MODEL.ASSIGNED : TAB_PANEL_MODEL.ADD);

    const tabs = tabsConfig.getTabsById(id);
    const addTab = tabs[TAB_PANEL_MODEL.ADD];
    const assignedTab = tabs[TAB_PANEL_MODEL.ASSIGNED];
    // Read only shows the assigned list alone, so the tab bar and the add view are not rendered
    const activeTab = disabled ? assignedTab?.key : subViewChild;

    return (
        <div className={`col-12 border-radius-8px px-0 ${mainContainerClass}`}>
            { id != null && !disabled &&
                <>
                    <CustomTabContainer
                        activeTab={subViewChild}
                        setActiveTab={(e) => {
                            setSearchElement("");
                            setResults(SEARCH_MODEL.clearResults());
                            setSelectForDelete([]);
                            setIsAllChecked(false);
                            setSubViewChild(e);
                        }}
                        className="border-0 px-3 d-flex justify-content-between"
                        paddingClass=''
                        buttonAction={
                            subViewChild === assignedTab?.key &&
                            <ButtonAction
                                id={deleteButtonId}
                                name='delete'
                                className={`btn-icon-delete h-100`}
                                Icon={TrashOutlineIcon}
                                disabled={selectForDelete.length <= 0}
                                onClick={() => setTriggerDelete(true)}
                            />
                        }
                    >
                        {tabs.map((tab) => (
                            <CustomTab
                                key={tab.key}
                                id={tab.key}
                                title={t(tab.translation)}
                                icon={tab.icon}
                                selected={subViewChild === tab.key}
                                onClick={() => setSubViewChild(tab.key)}
                            />
                        ))}
                    </CustomTabContainer>

                    <div className="border-bottom-shades-300 mb-3" />
                </>
            }
            {activeTab === addTab?.key &&
                <AddTabResource
                    useAddApp={useAddApp}
                    resource={resource}
                    id={id}
                    setBodyForExport={setBodyForExport}
                    results={results}
                    setResults={setResults}
                    searchElement={searchElement}
                    setSearchElement={setSearchElement}
                    setSearch={setSearch}
                    setSimpleSearch={setSimpleSearch}
                    modified={modified}
                    setModified={setModified}
                    selected={selected?.filter(e => !e?.isAssigned)}
                    setSelected={setSelected}
                    emptyFields={emptyFields}
                    autoHeight={autoHeight}
                />
            }

            {activeTab === assignedTab?.key &&
                <AssignedTabResource
                    useAssignedApp={useAssignedApp}
                    resource={resource}
                    id={id}
                    selected={selected}
                    setSelected={setSelected}
                    selectForDelete={selectForDelete}
                    setSelectForDelete={setSelectForDelete}
                    isAllChecked={isAllChecked}
                    setIsAllChecked={setIsAllChecked}
                    triggerDelete={triggerDelete}
                    setTriggerDelete={setTriggerDelete}
                    searchElement={searchElement}
                    setSearchElement={setSearchElement}
                    results={results}
                    setResults={setResults}
                    setBodyForExport={setBodyForExport}
                    setSearch={setSearch}
                    setSimpleSearch={setSimpleSearch}
                    modified={modified}
                    setModified={setModified}
                    readOnly={disabled}
                    setDisplayModal={setDisplayModal}
                    autoHeight={autoHeight}
                />
            }
        </div>
    );
}

export default TabPanel
